/**
 * TanStack Query hooks for `GET /reports/*` (project brief §21/§30).
 *
 * Same discipline as `queries.ts`: the visibility/tier scoping is entirely
 * server-side (`ReportsService.summary` populates only the tiers the
 * caller's own permissions unlock). These hooks never guess at a tier that
 * is missing from the response — every consumer checks for the key's
 * presence, not the current actor's role, before rendering a section.
 */
import { useQuery } from '@tanstack/react-query';
import type { ReportExportFormat, ReportExportScope, ReportSlaDto, ReportStatusChangesDto, ReportSummaryDto, ReportTrendsDto } from '@ithelpdesk/shared';
import { apiGet, requestRaw, shouldRetryQuery } from './client';

export const reportQueryKeys = {
  summary: () => ['reports', 'summary'] as const,
  trends: (params: ReportTrendsParams) => ['reports', 'trends', params] as const,
  sla: (params: ReportSlaParams) => ['reports', 'sla', params] as const,
  statusChanges: () => ['reports', 'status-changes'] as const,
};

export interface ReportTrendsParams {
  from?: string;
  to?: string;
  teamId?: string;
  bucket?: 'day' | 'week';
  /** IANA zone the day buckets are cut in (the viewer's); omitted = UTC. */
  tz?: string;
}

export interface ReportSlaParams {
  from?: string;
  to?: string;
  teamId?: string;
}

function toQuery(params: ReportTrendsParams | ReportSlaParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * `enabled: false` lets a caller that only wants a tier some actors hold
 * (e.g. the admin-tier system health in the sidebar) skip the request for
 * everyone else. The query key is shared, so every consumer on a page reads
 * the one cached response — never a duplicate request.
 */
export function useReportSummary(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: reportQueryKeys.summary(),
    enabled: options.enabled ?? true,
    queryFn: ({ signal }) => apiGet<ReportSummaryDto>('/reports/summary', { signal }),
    retry: shouldRetryQuery,
    // Dashboard KPIs don't need to-the-second freshness; avoids refetch storms
    // when several dashboard widgets mount at once.
    staleTime: 30_000,
  });
}

export function useReportTrends(params: ReportTrendsParams = {}) {
  return useQuery({
    queryKey: reportQueryKeys.trends(params),
    queryFn: ({ signal }) => apiGet<ReportTrendsDto>(`/reports/trends${toQuery(params)}`, { signal }),
    retry: shouldRetryQuery,
    staleTime: 30_000,
  });
}

export function useReportSla(params: ReportSlaParams = {}) {
  return useQuery({
    queryKey: reportQueryKeys.sla(params),
    queryFn: ({ signal }) => apiGet<ReportSlaDto>(`/reports/sla${toQuery(params)}`, { signal }),
    retry: shouldRetryQuery,
    staleTime: 30_000,
  });
}

/** `GET /reports/status-changes` — net per-status change over the last 24 h, for the KPI "from yesterday" lines. */
export function useReportStatusChanges(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: reportQueryKeys.statusChanges(),
    queryFn: ({ signal }) => apiGet<ReportStatusChangesDto>('/reports/status-changes', { signal }),
    enabled: options.enabled ?? true,
    retry: shouldRetryQuery,
    staleTime: 30_000,
  });
}

// ---- export -----------------------------------------------------------------

export type { ReportExportFormat, ReportExportScope };

export interface ReportExportRequest {
  format: ReportExportFormat;
  scope: ReportExportScope;
  /** IANA zone the report's dates are cut in (the viewer's). */
  tz?: string | undefined;
}

/** File name from a `Content-Disposition` header (RFC 6266), or null. */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // fall through to the plain form
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
}

/** Saves a blob through a temporary `<a download>` and revokes the object URL shortly after. */
export function saveBlobAs(blob: Blob, name: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Give the browser a moment to start the download before freeing the blob.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  }
}

/**
 * `GET /reports/export` as a browser download: fetch with the session cookie,
 * hold the bytes as a blob, click a temporary `<a download>` and revoke the URL.
 * Which scope/format a role may request is enforced by the server (403/400/429/422
 * arrive as the standard `ApiError`); the buttons that call this are UX only.
 * Resolves to the saved file name.
 */
export async function downloadReportExport(req: ReportExportRequest): Promise<string> {
  const qs = new URLSearchParams({ format: req.format, scope: req.scope });
  if (req.tz) qs.set('tz', req.tz);
  const response = await requestRaw(`/reports/export?${qs.toString()}`);
  const blob = await response.blob();
  const name = filenameFromContentDisposition(response.headers.get('Content-Disposition')) ?? `${req.scope}-report.${req.format}`;
  saveBlobAs(blob, name);
  return name;
}
